import { environment } from '@env/environment';
import moment from 'moment';
import { AbstractControl, FormControl, ValidationErrors } from '@angular/forms';
import { PhoneNumberUtil } from 'google-libphonenumber';

export function apiUrl(path?: string) {
  return environment.apiUrl + '/' + path;
}

export function bootstrapToDatabase(date) {
  return `${date.year}-${addZeroToMonthAndDayCount(date.month)}-${addZeroToMonthAndDayCount(date.day)}`;
}

function addZeroToMonthAndDayCount(number: number): string {
  return number < 10 ? '0' + number : number.toString();
}

export function stringDateToDatabase(dateString) {
  if (dateString == null) {
    return null;
  }

  const date = new Date(dateString);
  return bootstrapToDatabase({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  });
}

export function stringLocalDateToDatabase(dateString) {
  if (dateString == null) {
    return null;
  }

  const date = new Date(dateString);
  return bootstrapToDatabase({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  });
}

export function dayNumtoDay(day) {
  const days = [];
  days[0] = 'Sunday';
  days[1] = 'Monday';
  days[2] = 'Tuesday';
  days[3] = 'Wednesday';
  days[4] = 'Thursday';
  days[5] = 'Friday';
  days[6] = 'Saturday';
  return days[day];
}

export function databaseToBootstrap(D) {
  if (!D) {
    return null;
  }
  const date = new Date(D);
  const year = date.getUTCFullYear();
  const day = date.getUTCDate();
  const month = [];
  month[0] = 'January';
  month[1] = 'February';
  month[2] = 'March';
  month[3] = 'April';
  month[4] = 'May';
  month[5] = 'June';
  month[6] = 'July';
  month[7] = 'August';
  month[8] = 'September';
  month[9] = 'October';
  month[10] = 'November';
  month[11] = 'December';
  const month_name = month[date.getUTCMonth()];
  return {
    day: day,
    month: month_name,
    year: year,
  };
}

export function dateOnly(date) {
  return date.setHours(0, 0, 0, 0);
}

export function validatePhoneNumber(): any {
  return (control: FormControl): { [key: string]: any } | null => {
    const phoneNumberUtil = PhoneNumberUtil.getInstance();
    const phoneNumberString = control.value;
    try {
      // const defaultCountry = 'US';
      const phoneNumber = phoneNumberUtil.parseAndKeepRawInput(phoneNumberString);
      const isValidNumber = phoneNumberUtil.isValidNumber(phoneNumber);

      return isValidNumber ? null : { phone_no: { value: phoneNumberString } };
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return { phone_no: { value: phoneNumberString } };
    }
  };
}
export function capitalizeFirstCharacterInWord(value: any): string {
  if (!value) {
    return value;
  }
  const words = value.split(' ');
  if (words.length > 0) {
    words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
  }
  return words.join(' ');
}

export function futureDateValidator(control: AbstractControl) {
  if (!control.value) return null; // Allow empty values

  const selectedDate = moment(control.value, ['MM/DD/YYYY', 'YYYY-MM-DD'], true);
  const today = moment();

  return selectedDate.isValid() && selectedDate.isAfter(today) ? { futureDate: true } : null;
}

export function groupCategoriesByType(data: any[]) {
  const grouped = data.reduce((acc, current) => {
    const type = current.type;
    const existingGroup = acc.find((group) => group.type === type);
    if (existingGroup) {
      existingGroup.categories.push({ ...current, isOpen: false });
    } else {
      acc.push({
        type: type,
        categories: [{ ...current, isOpen: false }],
        isOpen: false,
      });
    }
    return acc;
  }, []);
  return grouped;
}

export function periodContainsMonthAndYear(value) {
  if (!value) return false;
  const monthYearPattern =
    /^(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december) \d{4}$/i;
  value = value.replace(/^(\w{3,9})/, (match) => match.charAt(0).toUpperCase() + match.slice(1).toLowerCase());
  return monthYearPattern.test(value);
}

export function atLeastOne(control: AbstractControl): ValidationErrors | null {
  const values = Object.values(control.value);
  const hasValue = values.some((value) => value !== null && value !== '');

  return hasValue ? null : { atLeastOne: true };
}

export function birthdateValidator(control: AbstractControl) {
  const birthdate = new Date(control.value);
  const today = new Date();
  if (birthdate > today) {
    return { invalidBirthdate: true };
  }
  return null;
}

export function formatTimestamp(timestamp: number): { datePart: string; timePart: string } {
  const date = new Date(0);
  date.setUTCSeconds(timestamp);
  return {
    datePart: date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    timePart: date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }),
  };
}

export const prefixes = [
  'Mr.',
  'Mrs.',
  'Miss',
  'Ms.',
  'Dr.',
  'Prof.',
  'Hon.',
  'Rev.',
  'Fr.',
  'Sr.',
  'Br.',
  'Rabbi',
  'Imam',
  'Pastor',
  'Bishop',
  'Monsignor',
  'Cardinal',
  'Archbishop',
  'Gen.',
  'Col.',
  'Maj.',
  'Capt.',
  'Lt.',
  'Cmdr.',
  'Sen.',
  'Rep.',
  'Gov.',
  'Pres.',
  'Amb.',
];

export const suffixes = [
  'Jr.',
  'Sr.',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
  'X',
  'Esq.',
  'MD',
  'DO',
  'DDS',
  'DVM',
  'PhD',
  'JD',
  'CPA',
  'Atty.',
  'Eng.',
  'PE',
];

export function formatISODateToReadable(isoDateString: string): { datePart: string; timePart: string } {
  if (!isoDateString) { return { datePart: '', timePart: '' } };
  const date = new Date(isoDateString);
  if (isNaN(date.getTime())) { return { datePart: '', timePart: '' } };
  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return { datePart, timePart };
}

export function calculateAge (birthdate){
  if (!birthdate) { return null; }
  const today = new Date();
  const birthDate = new Date(birthdate);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export function extractYear(dateString: string): string {
  if (!dateString) { return ''; }
  const date = new Date(dateString);
  if (!isNaN(date.getTime())) {
    return date.getFullYear().toString();
  }
  const yearMatch = dateString.match(/\b(\d{4})\b/);
  return yearMatch ? yearMatch[1] : dateString.substring(0, 4);
}

export function formatCurrency(value: any): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value || 0);
}

export function formatPercentage(value: any, showPercentageIcon: boolean = true): string {
  const numericValue = Number(value) || 0;
  if (!showPercentageIcon) {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numericValue);
  }
  return `${numericValue.toFixed(2)}%`;
}

export function convertSharesToPercentage(value: number, totalShares: number): number {
  if ((!value && value !== 0) || !totalShares) {
    return 0;
  }
  return (Number(value) / Number(totalShares)) * 100;
}

 const relationshipOrder = [
  'Spouse',
  'Husband', 
  'Wife',
  'Fiancee',
  'Significant Other',
  'Civil Partner',
  'Domestic Partner',
  'Girlfriend',
  'Boyfriend',
  'Child',
  'Son',
  'Daughter',
  'Step-Son',
  'Step-Daughter',
  'Son In-Law',
  'Daughter In-Law',
  'Parent',
  'Mother',
  'Father',
  'Mother In-Law',
  'Father In-Law',
  'Step-Mother',
  'Step-Father',
  'Sibling',
  'Sister',
  'Brother',
  'Sister In-Law',
  'Brother In-Law',
  'Step-Sister',
  'Step-Brother',
  'Grandchild',
  'Grandson',
  'Granddaughter',
  'Step-Grandson',
  'Step-Granddaughter',
  'Great-Grandchild',
  'Great-Grandson',
  'Great-Granddaughter',
  'Great-great-Grandchild',
  'Great-great-Grandson',
  'Great-great-Granddaughter',
  'Grandparent',
  'Grandfather',
  'Grandmother',
  'Step-Grandmother',
  'Step-Grandfather',
  'Great-Grandparent',
  'Great-Grandfather',
  'Great-Grandmother',
  'Great-great-Grandparent',
  'Great-great-Grandfather',
  'Great-great-Grandmother',
  'Aunt',
  'Uncle',
  'Paternal Aunt',
  'Paternal Uncle',
  'Maternal Aunt',
  'Maternal Uncle',
  'Great-Aunt',
  'Great-Uncle',
  'Great-great-Aunt',
  'Great-great-Uncle',
  'Great-Paternal Aunt',
  'Great-Paternal Uncle',
  'Great-Maternal Aunt',
  'Great-Maternal Uncle',
  'Great-great-Paternal Aunt',
  'Great-great-Paternal Uncle',
  'Great-great-Maternal Aunt',
  'Great-great-Maternal Uncle',
  'Niece',
  'Nephew',
  'Great-Niece',
  'Great-Nephew',
  'Great-great-Niece',
  'Great-great-Nephew',
  'Cousin',
  'In-Law',
  'Non Immediate Family',
  'Friend',
  'Co-habitant',
  'Other Family Member',
  'Co-worker',
  'Roommate',
  'Legal Dependent',
  'Business Partner',
  'Legal Guardian',
  'Professional - Legal',
  'Professional - Financial',
  'Professional - Insurance',
  'Professional - Medical',
  'Professional - General',
  'Professional - Assistant',
  'Professional - Other',
  'Professional - Staff',
  'Meta Advisor',
  'Mentor',
  'Advisor',
  'Client',
  'Patient',
  'Associate',
  'Other'
];

export const MY_FORMATS = {
  parse: {
    dateInput: 'YYYY-MM-DD',
  },
  display: {
    dateInput: 'MMM D, YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export const relationshipOrderMap = new Map(
  relationshipOrder.map((relationship, index) => [relationship, index])
);

export function getRelationshipOrderIndex(relationshipName: string): number {
  return relationshipOrderMap.get(relationshipName) ?? relationshipOrder.length;
}

export function getFileExtension(fileName: string) {
  if (!fileName) {
    return 'doc';
  }
  const type = fileName.replace(/^.*\./, '') !== fileName ? fileName.replace(/^.*\./, '') : '';
  if (type === '') {
    return 'doc';
  } else {
    return type.toLowerCase();
  }
}

export function formatDate(dateString: string): string {
  if (!dateString) {
    return '';
  }

  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  const date = m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])) : new Date(dateString);

  if (isNaN(date.getTime())) {
    return dateString;
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function parseAmountValue(amountValue: any): number {
  if (amountValue === null || amountValue === undefined) {
    return 0;
  }
  let valueString = amountValue.toString();
  if (valueString.startsWith('(') && valueString.endsWith(')')) {
    valueString = valueString.slice(1, -1);
  }
  valueString = valueString.replace(/[$%,]/g, '');
  const numericValue = parseFloat(valueString);
  return isNaN(numericValue) ? 0 : numericValue;
}

// Export XSS security utilities
export {
  validateUserInput,
  sanitizeMessageContent,
  stripHtmlTags,
  sanitizePlainTextStrict,
  ValidationResult,
} from './xss-security.util';